import type { SkillKind } from './data/skills';
import type { Rng } from './rng';

export const COMBAT = {
  /** Damage fraction that gets through a block. */
  blockDamageMult: 0.1,
  /** Stamina drained from the blocker per point of incoming skill power. */
  blockStaminaPerPower: 14,
  /**
   * Seconds from a blow's start to the moment the blade reaches the target (the strike key the renderer shows:
   * the wind-up held, then the cut; apps/client director.ts). Damage, the hit event and the hit-stop land then,
   * and a stun or stagger taken before it (a break, a parried blow) cancels the blow.
   */
  strikeDelay: 0.19,
  /** The same for a leaping strike (it lands as the leap comes down) and an ultimate (after its roar). */
  strikeDelayLeap: 0.26,
  strikeDelayPower: 0.4,
  /** How far past its reach a struck target may have moved by then and still be hit. */
  strikeSlack: 0.8,
  /** Attacker stagger after being blocked: the window to punish them. */
  deflectStagger: 0.3,
  guardBreakMult: 1.5,
  guardBreakDefaultStun: 1.2,
  /** Guard collapses when a blocked hit drains the last of the stamina. */
  exhaustedGuardMult: 0.5,
  exhaustedGuardStun: 1,
  interruptMult: 1.25,
  interruptStun: 0.8,
  /** Break skills are weak against an opponent who is not blocking. */
  breakUnguardedMult: 0.8,
  critMult: 1.75,
  dashStaminaCost: 25,
  dashDistance: 4,
  dashDuration: 0.2,
  dashInvulnerable: 0.4,
  /** Evasion chance: base plus 1% per point of dodge above the attacker's accuracy, capped. */
  evadeBase: 0.03,
  evadePerPoint: 0.01,
  evadeMax: 0.3,
} as const;

/**
 * `guardShatter` and `countered` come only from a duel bout (systems/duelMatch.ts `duelMod`): the guard's points ran out under
 * a blow (the blow is cut as a guarded one, then the guard breaks), or a counter stance undid the blow (no damage).
 */
export type HitOutcome = 'hit' | 'blocked' | 'guardBreak' | 'interrupt' | 'dodge' | 'guardShatter' | 'countered';

export interface HitSpec {
  atk: number;
  power: number;
  kind: SkillKind;
  /** Stun when shattering a guard (break skills). */
  stun?: number;
  critChance: number;
  /** Accuracy against the defender's dodge. Omit to skip the evasion roll. */
  hit?: number;
}

export interface DefenderState {
  blocking: boolean;
  stamina: number;
  invulnerable: boolean;
  /** Kind of skill the defender is currently charging, if any. */
  castingKind: SkillKind | null;
  def: number;
  /** Evasion rating. Omit to skip the evasion roll. */
  dodge?: number;
}

export interface HitResult {
  outcome: HitOutcome;
  damage: number;
  staminaDamage: number;
  stun: number;
  crit: boolean;
  /** The bout's burst cap ate the whole blow: it did no harm, so no stun and no status come with it. */
  spent?: boolean;
}

const dmg = (n: number) => Math.max(1, Math.round(n));

/** Resolves one hit through the attack / break / block triangle. Pure; the caller applies the result. */
export function resolveHit(h: HitSpec, d: DefenderState, rng: Rng): HitResult {
  if (d.invulnerable) return { outcome: 'dodge', damage: 0, staminaDamage: 0, stun: 0, crit: false };
  // A fighter standing guard does not sidestep; otherwise accuracy contests evasion.
  if (h.hit !== undefined && d.dodge !== undefined && !d.blocking) {
    const evade = Math.min(
      COMBAT.evadeMax,
      Math.max(0, COMBAT.evadeBase + (d.dodge - h.hit) * COMBAT.evadePerPoint),
    );
    if (rng() < evade) return { outcome: 'dodge', damage: 0, staminaDamage: 0, stun: 0, crit: false };
  }

  const crit = rng() < h.critChance;
  const variance = 0.9 + rng() * 0.2;
  const raw = h.atk * h.power * variance * (crit ? COMBAT.critMult : 1) * (100 / (100 + Math.max(0, d.def)));

  // Power moves (ท่าพลัง) cannot be blocked: they land in full through a guard.
  if (h.kind === 'power') return { outcome: 'hit', damage: dmg(raw), staminaDamage: 0, stun: 0, crit };

  if (d.blocking) {
    if (h.kind === 'break')
      return {
        outcome: 'guardBreak',
        damage: dmg(raw * COMBAT.guardBreakMult),
        staminaDamage: d.stamina,
        stun: h.stun ?? COMBAT.guardBreakDefaultStun,
        crit,
      };
    const drain = COMBAT.blockStaminaPerPower * h.power;
    if (drain >= d.stamina)
      return {
        outcome: 'guardBreak',
        damage: dmg(raw * COMBAT.exhaustedGuardMult),
        staminaDamage: d.stamina,
        stun: COMBAT.exhaustedGuardStun,
        crit: false,
      };
    return {
      outcome: 'blocked',
      damage: dmg(raw * COMBAT.blockDamageMult),
      staminaDamage: drain,
      stun: 0,
      crit: false,
    };
  }

  if (h.kind === 'attack' && d.castingKind === 'break')
    return {
      outcome: 'interrupt',
      damage: dmg(raw * COMBAT.interruptMult),
      staminaDamage: 0,
      stun: COMBAT.interruptStun,
      crit,
    };

  if (h.kind === 'break')
    return { outcome: 'hit', damage: dmg(raw * COMBAT.breakUnguardedMult), staminaDamage: 0, stun: 0, crit };

  return { outcome: 'hit', damage: dmg(raw), staminaDamage: 0, stun: 0, crit };
}
